import { AddCircle, Delete, Edit, Save } from "@material-ui/icons";
import { IconButton } from "@mui/material";
import { useState } from "react";
import { Col, Form, InputGroup, Modal, Row, Table } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import useOrder from "../../../context/OrderContext";
import { useLazyGetSelectedOrderDetailsQuery } from "../../../features/Dispatch/DispatchSlice";
import { useAddProductToOrderMutation, useDeletePartialProductDescriptionMutation, useDeleteProductDescriptionMutation, useEditProductDescriptionMutation, useLazyGetHazardousMaterialsListQuery } from "../../../features/order/orderSlice";
import { addProduct, deleteProduct, editProduct } from "../../../features/product/productDataSlice";
import { useGetListOfUnitQuery } from "../../../features/unit/unitSlice";
import errorHandler from "../../hooks/ErrorHanlder";
import Button from "../../designSystem/Button/Button";

const initialFormValues = {
  productTypeId: 0,
  stackable: false,
  noOfUnits: "",
  productDescription: "",
  productref: "",
  productweight: "",
  length: 0,
  width: 0,
  height: 0,
  cubicFT: "",
  linearFt: "",
  dimweight: "",
  spots: "",
  hazClass: 0,
  unNumber: "",
  packingGroup: "",
  partialPackages: []
};

const Productdesp = ({ sizeLg = false, variant = "primary", divisionId, orderId }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { data: productsListData } = useGetListOfUnitQuery();
  const { productLists, ActualWeight, orderInfo } = useOrder();
  const [lgShow, setLgShow] = useState(false);
  const [editingTaskIndex, setEditingTaskIndex] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [getOrderProductDetails, orderProductDetails] = useLazyGetSelectedOrderDetailsQuery();
  const orderDetails = orderProductDetails?.data?.data
  const products = orderProductDetails?.data?.data?.orderProductDescriptionsList
  const [addProductToOrder] = useAddProductToOrderMutation()
  const [editProductDescription] = useEditProductDescriptionMutation()
  const [deleteProductDescription] = useDeleteProductDescriptionMutation()
  const [deletePartialProductDescription] = useDeletePartialProductDescriptionMutation()
  const { register, handleSubmit, reset, watch, control, clearErrors, formState: { errors }, getValues } = useForm({
    defaultValues: initialFormValues
  });
  const [getHazardousMaterialsList, Results] = useLazyGetHazardousMaterialsListQuery();
  const hazardousMaterialsList = Results?.data

  const { fields, append, remove } = useFieldArray({ control, name: "partialPackages" });
  const noOfUnitsVal = Number(watch("noOfUnits") || 0);
  const partialPackagesVal = watch("partialPackages") || [];

  const sumOfNoOfPackages = partialPackagesVal.reduce(
    (acc, pkg) => acc + Number(pkg.noOfPackages || 0),
    0
  );
  const lengthVal = Number(watch("length") || 0);
  const widthVal = Number(watch("width") || 0);
  const heightVal = Number(watch("height") || 0);
  const weightDensity = Number(watch("productweight") || 0);

  const cubicFTCalculatedValue = lengthVal * widthVal * heightVal;
  const linearFtCalculatedValue = ((lengthVal * widthVal) / 144) / 16;
  const dimWeightFtCalculatedValue = ((lengthVal * widthVal * heightVal) / 1728) * weightDensity;
  const shouldCallApi = !pathname.includes("/order/order-taking-by-telephone");
  const productsRows = shouldCallApi ? products : productLists;
  const productList = shouldCallApi ? orderDetails?.orderProductDescriptionsList : productLists
  const totalPcs = shouldCallApi ? orderDetails?.pcs : orderInfo.pcs

  const onSubmit = async (formData) => {
    const updatedUnNumber = formData.unNumber ? `UN${formData.unNumber}` : "";
    let submittedUnits = productList.reduce(
      (acc, prod) => acc + Number(prod.noOfUnits || 0),
      0
    );

    if (editingTaskIndex !== null) {
      const productToEdit = productsRows.find((prod) =>
        shouldCallApi
          ? prod.productid === editingTaskIndex
          : prod.id === editingTaskIndex
      );
      if (productToEdit) {
        submittedUnits -= Number(productToEdit.noOfUnits || 0);
      }
    }

    if (submittedUnits + Number(formData.noOfUnits || 0) > totalPcs) {
      toast.error(`Total units exceed the allowed limit of ${totalPcs}`, { position: "top-center" });
      return;
    }

    const newProduct = {
      ...formData,
      unNumber: updatedUnNumber,
      cubicFT: cubicFTCalculatedValue,
      linearFt: linearFtCalculatedValue,
      dimweight: dimWeightFtCalculatedValue,
      width: isNaN(formData.width) || formData.width == null ? 0 : Number(formData.width),
      height: isNaN(formData.height) || formData.height == null ? 0 : Number(formData.height),
      length: isNaN(formData.length) || formData.length == null ? 0 : Number(formData.length),
      orderId
    };

    const actionMap = {
      add: {
        apiCall: addProductToOrder,
        reduxAction: addProduct,
        successMessage: 'Added successfully'
      },
      edit: {
        apiCall: editProductDescription,
        reduxAction: editProduct,
        successMessage: 'Updated successfully'
      }
    };

    const mode = editingTaskIndex === null ? 'add' : 'edit';
    const { apiCall, reduxAction, successMessage } = actionMap[mode];

    if (shouldCallApi) {
      try {
        const result = await apiCall(newProduct).unwrap();
        if (result?.statusCode === 200) {
          toast.success(successMessage, { position: "top-center" });
          getOrderProductDetails({ divisionId, orderId });
        } else {
          errorHandler(result);
        }
      } catch (err) {
        errorHandler(err);
      }
    } else {
      dispatch(reduxAction(newProduct));
    }

    reset(initialFormValues);
    setEditingTaskIndex(null);
    setIsUpdate(false);
  };

  const editProductList = (id) => {
    const productToEdit = productsRows?.find((prod) =>
      shouldCallApi ? prod.productid === id : prod.id === id
    );
    if (productToEdit) {
      const unNumberValue =
        productToEdit.unNumber && productToEdit.unNumber.startsWith("UN")
          ? productToEdit.unNumber.slice(2)
          : productToEdit.unNumber;

      reset({
        ...productToEdit,
        unNumber: unNumberValue,
      });
      setEditingTaskIndex(id);
      setIsUpdate(true);
    }
  };

  const handleAddPackage = () => {
    const lastRow = partialPackagesVal[partialPackagesVal.length - 1];

    const sumOfNoUnits = productLists.reduce(
      (acc, prod) => acc + Number(prod.noOfUnits || 0),
      0
    ) + Number(watch("noOfUnits") || 0);

    if (sumOfNoUnits > orderInfo.pcs) {
      return toast.error(`Total no of Units exceed the allowed limit ${orderInfo.pcs}`, { position: "top-center" });
    }
    if (sumOfNoOfPackages >= noOfUnitsVal || sumOfNoOfPackages > orderInfo.pcs) {
      return toast.error(`Total no of Packages exceed the allowed limit Number of Units`, { position: "top-center" });
    }

    if (partialPackagesVal.length > 0) {
      if (
        !lastRow.packageTypeId ||
        !lastRow.packageName ||
        !lastRow.noOfPackages
      ) {
        return toast.error("Please fill in the current package details before adding a new one.", { position: "top-center" });
      }
    }

    append({ packageTypeId: "", packageName: "", noOfPackages: "" });
  };

  const handleDelete = async (data) => {
    if (shouldCallApi) {
      try {
        const result = await deleteProductDescription({ productId: data.productid, }).unwrap();
        if (result?.statusCode === 200) {
          toast.success('Deleted successfully', { position: "top-center" });
          getOrderProductDetails({ divisionId, orderId });
        } else {
          errorHandler(result)
        }
      } catch (err) {
        errorHandler(err);
      }
    } else {
      dispatch(deleteProduct(data.id))
    }
  };

  const partialDelete = async (index) => {
    if (shouldCallApi && fields[index]?.partialProductId) {
      try {
        const result = await deletePartialProductDescription({ partialProductId: fields[index].partialProductId }).unwrap();
        if (result?.statusCode === 200) {
          toast.success('Deleted successfully', { position: "top-center" });
          getOrderProductDetails({ divisionId, orderId });
          remove(index)
        } else {
          errorHandler(result)
        }
      } catch (err) {
        errorHandler(err);
      }

    } else {
      remove(index)
    }
  }

  return (
    <>
      <Button
        style={{ height: variant === "primary" && "100%" }}
        size={sizeLg ? "lg" : "md"}
        variant={variant}
        onClick={() => {
          getOrderProductDetails({ divisionId, orderId });
          getHazardousMaterialsList();
          setLgShow(true)
        }}
        fullWidth={false}
      >
        Product Description
      </Button>
      <Modal
        size="lg"
        show={lgShow}
        onHide={() => {
          setLgShow(false)
          reset(initialFormValues);
          setEditingTaskIndex(null);
          setIsUpdate(false);
        }}
        aria-labelledby="example-modal-sizes-title-lg"
        id="product-desp"
      >
        <Modal.Header closeButton>
          <Modal.Title id="example-modal-sizes-title-lg">Product Description</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="mt-4 mb-2">
              <Col sm={10}>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="2">
                    Actual Weight
                  </Form.Label>
                  <Col sm="4">
                    <Form.Control
                      type="text"
                      placeholder="Actual weight"
                      value={ActualWeight || 0}
                      readOnly
                    />
                  </Col>
                </Form.Group>
              </Col>
              <Col className="mb-2" sm={isUpdate ? 1 : 2}>
                <div className="addaccessrial_btn">
                  <button type="submit" className="btn">
                    {isUpdate ? <Save /> : <AddCircle />}
                  </button>
                </div>
              </Col>
              {isUpdate ? <Col sm={1}>
                <button size="sm" className="btn btn-outline-primary" onClick={() => {
                  reset(initialFormValues);
                  setEditingTaskIndex(null);
                  setIsUpdate(false);
                }}>Cancel</button>
              </Col> : null}
            </Row>
            <fieldset className="mb-2">
              <div className="product-input">
                <div className="product-input-box">
                  <label>Packages</label>
                  <IconButton onClick={handleAddPackage} >
                    <AddCircle htmlColor="#244da1" />
                  </IconButton>
                </div>
                <div className="product-input-box">
                  <label htmlFor="stackable">Stackable</label>
                  <input type="checkbox" id="stackable" {...register("stackable")} />
                </div>
                <div className="flex-column">
                  <div className="d-flex">
                    <div className="product-input-box">
                      <label htmlFor="productTypeId">Product Type</label>
                      <select aria-label="Floating label select example" {...register("productTypeId", { valueAsNumber: true, required: 'Field is required' })} style={{ border: "1px solid #d5d4d4", borderRadius: "4px", padding: "10px 35px 10px 5px" }}>
                        <option value={0}>Select</option>:
                        <>
                          {
                            productsListData?.data?.map((data, index) => (
                              <option key={index} value={data?.unitID}>{data?.unitType}</option>
                            ))
                          }
                        </>
                        {errors.productTypeId && (
                          <span style={{ color: "red", fontSize: "0.8rem" }}>
                            {errors.productTypeId.message}
                          </span>
                        )}
                      </select>
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="noOfUnits">Number of Units</label>
                      <input type="number" id="noOfUnits" {...register("noOfUnits", { valueAsNumber: true, required: 'Field is required' })} min="0" />
                      {errors.noOfUnits && (
                        <span style={{ color: "red", fontSize: "0.8rem" }}>
                          {errors.noOfUnits.message}
                        </span>
                      )}
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="productDescription">Product Description</label>
                      <input type="text" id="productDescription" {...register("productDescription")} />
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="productref">Product Reference</label>
                      <input type="text" id="productref" {...register("productref", { required: 'Field is required' })} />
                      {errors.productref && (
                        <span style={{ color: "red", fontSize: "0.8rem" }}>
                          {errors.productref.message}
                        </span>
                      )}
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="productweight">Product Weight</label>
                      <input type="number" id="productweight" {...register("productweight", { valueAsNumber: true, required: 'Field is required' })} min="0" />
                      {errors.productweight && (
                        <span style={{ color: "red", fontSize: "0.8rem" }}>
                          {errors.productweight.message}
                        </span>
                      )}
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="length">Length</label>
                      <input type="number" id="length" {...register("length", { valueAsNumber: true })} min="0" />
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="width">Width</label>
                      <input type="number" id="width" {...register("width", { valueAsNumber: true })} min="0" />
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="height">Height</label>
                      <input type="number" id="height" {...register("height", { valueAsNumber: true })} min="0" />
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="cubicFT">Cubic Ft</label>
                      <input type="number" id="cubicFT" {...register("cubicFT", { valueAsNumber: true })} min="0" disabled value={cubicFTCalculatedValue || ""} />
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="linearFt">Linear Ft</label>
                      <input type="number" id="linearFt" {...register("linearFt", { valueAsNumber: true })} min="0" disabled value={linearFtCalculatedValue || ""} />
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="dimweight">Dim weight</label>
                      <input type="number" id="dimweight" {...register("dimweight", { valueAsNumber: true })} min="0" disabled value={dimWeightFtCalculatedValue || ""} />
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="spots">Spots</label>
                      <input type="number" id="spots" {...register("spots", { valueAsNumber: true, required: 'Field is required' })} min="0" />
                      {errors.spots && (
                        <span style={{ color: "red", fontSize: "0.8rem" }}>
                          {errors.spots.message}
                        </span>
                      )}
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="HazClass">Haz Class</label>
                      <select aria-label="Floating label select example"  {...register("hazClass", { valueAsNumber: true })} style={{ border: "1px solid #d5d4d4", borderRadius: "4px", padding: "6px 12px", height: "38px" }}>
                        <>
                          <option value={0}>Select</option>
                          {
                            hazardousMaterialsList?.map((data) => (
                              <option key={data?.id} value={data?.id}>{data?.classDivision}</option>
                            ))
                          }
                        </>
                      </select>
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="unNumber">UN#</label>
                      <InputGroup className="flex-nowrap">
                        <InputGroup.Text>UN</InputGroup.Text>
                        <Form.Control
                          style={{ minWidth: "100px" }}
                          type="text"
                          id="unNumber"
                          {...register("unNumber", {
                            validate: (value) => {
                              const hazClass = watch("hazClass");
                              if (hazClass !== 0 && (!value || value.trim() === "")) {
                                return "UN# is required if Haz Class is selected.";
                              }
                              return true;
                            }
                          })}
                        />
                      </InputGroup>
                      {errors.unNumber && (
                        <span style={{ color: "red", fontSize: "0.8rem" }}>
                          {errors.unNumber.message}
                        </span>
                      )}
                    </div>
                    <div className="product-input-box">
                      <label htmlFor="packingGroup">Packing Group</label>
                      <input
                        type="text"
                        id="packingGroup"
                        {...register("packingGroup", {
                          pattern: {
                            value: /^(I{1,3})?$/,
                            message: "Packing Group must be blank or Roman numerals I, II, III"
                          }
                        })}
                      />
                      {errors.packingGroup && (
                        <span style={{ color: "red", fontSize: "0.8rem" }}>
                          {errors.packingGroup.message}
                        </span>
                      )}
                    </div>
                  </div>
                  {fields.length > 0 && (
                    <>
                      {fields.map((field, index) => (
                        <Row key={field.id}>
                          <Col sm={1} style={{ paddingLeft: "22px", display: "flex", flexDirection: "column" }}>
                            <label htmlFor={`partialPackages.${index}.packageTypeId`}>Package Type</label>
                            <select aria-label="Floating label select example"  {...register(`partialPackages.${index}.packageTypeId`, { valueAsNumber: true })} style={{ border: "1px solid #d5d4d4", borderRadius: "4px", padding: "6px 12px", height: "38px" }}>
                              <option>Select</option>:
                              <>
                                {
                                  productsListData?.data?.map((data, index) => (
                                    <option key={index} value={data?.unitID}>{data?.unitType}</option>
                                  ))
                                }
                              </>
                            </select>
                          </Col>
                          <Col sm={1}>
                            <Form.Group controlId={`partialPackages.${index}.packageName`}>
                              <label>Package Name</label>
                              <Form.Control type="text" {...register(`partialPackages.${index}.packageName`)} />
                            </Form.Group>
                          </Col>
                          <Col sm={1}>
                            <Form.Group controlId={`partialPackages.${index}.noOfPackages`}>
                              <label>No Of Packages</label>
                              <Form.Control
                                type="number"
                                {...register(`partialPackages.${index}.noOfPackages`, {
                                  valueAsNumber: true,
                                  min: {
                                    value: 0,
                                    message: "Cannot be negative",
                                  },
                                  validate: () => {
                                    if (sumOfNoOfPackages > noOfUnitsVal) {
                                      return "Cannot be greater than no of units"
                                    } else {
                                      clearErrors(`partialPackages.${index}.noOfPackages`);
                                    }
                                  },
                                })}
                              />
                              {errors?.partialPackages?.[index]?.noOfPackages?.message && (
                                <span style={{ color: "red", fontSize: "0.8rem" }}>
                                  {errors?.partialPackages?.[index]?.noOfPackages?.message}
                                </span>
                              )}
                            </Form.Group>
                          </Col>
                          <Col sm={1} className={`d-flex ${errors?.partialPackages?.[index]?.noOfPackages?.message ? "align-items-center" : "align-items-end"}`}>
                            <Button style={{ height: "38px" }} variant="danger" onClick={() => partialDelete(index)}>
                              Delete
                            </Button>
                          </Col>
                        </Row>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </fieldset>
          </Form>
          <Row className="product-table">
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Action</th>
                  <th>No Of Units</th>
                  <th>Product Description</th>
                  <th>Product Reference</th>
                  <th>Product Weight</th>
                  <th>Length</th>
                  <th>Width</th>
                  <th>Height</th>
                  <th>CubicFT</th>
                  <th>linearFt</th>
                  <th>Dimweight</th>
                  <th>Spots</th>
                  <th>hazClass</th>
                  <th>unNumber</th>
                  <th>packingGroup</th>
                </tr>
              </thead>
              <tbody>
                {productsRows?.map((data) => (
                  <tr key={data.id}>
                    <td>
                      <div className="action_btn">
                        <span className="edit_btn" onClick={() => {
                          editProductList(shouldCallApi ? data.productid : data.id)
                        }}>
                          <Edit />
                        </span>
                        <span className="del_btn" onClick={() => handleDelete(data)}>
                          <Delete />
                        </span>
                      </div>
                    </td>
                    <td>{data.noOfUnits}</td>
                    <td>{data.productDescription}</td>
                    <td>{data.productref}</td>
                    <td>{data.productweight}</td>
                    <td>{data.length}</td>
                    <td>{data.width}</td>
                    <td>{data.height}</td>
                    <td>{data.cubicFT}</td>
                    <td>{data.linearFt}</td>
                    <td>{data.dimweight}</td>
                    <td>{data.spots}</td>
                    <td>{data.hazClass !== 0 ? hazardousMaterialsList?.[data.hazClass]?.classDivision : 0}</td>
                    <td>{data.unNumber}</td>
                    <td>{data.packingGroup}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Row>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Productdesp;